// packages/web/src/components/FrameBack.tsx — CV5 §2: ONE ESCAPE, ONE PLACE.
//
// The frame (the phone's `.app-topbar`) renders the way back when the current
// route has a parent. This context is how every screen inside it learns to
// stop rendering its own — the spec's choice between "the frame replaces the
// screen-local one" and "the screen suppresses its own" resolved in favour of
// the frame, with the screens told rather than asked.
//
// WHY A CONTEXT AND NOT A PROP. The rail layout renders no top bar at all, so
// the answer is not a constant: on a desktop rail (and inside the two-pane
// master/detail, where `onBack` means "de-select", not "leave") the screens
// must keep their own control. A prop would have to be threaded through ten
// screens and every one of them could forget it — which is the exact failure
// mode §2 exists to end. The default is FALSE, so a screen mounted on its own
// (every screen test, the two-pane detail pane) behaves exactly as it did
// before this chunk.
import { createContext } from 'preact';
import type { ComponentChildren } from 'preact';
import { useContext } from 'preact/hooks';

const FrameBackContext = createContext(false);

export interface FrameBackProviderProps {
  /** True when the frame is rendering a back affordance for this route. */
  owns: boolean;
  children: ComponentChildren;
}

export function FrameBackProvider({ owns, children }: FrameBackProviderProps) {
  return <FrameBackContext.Provider value={owns}>{children}</FrameBackContext.Provider>;
}

/** Whether the frame around this screen is already showing the way back. A
 * screen that renders its own escape must consult this and render nothing
 * when it answers true — two ways back is the defect, not the fix. */
export function useFrameOwnsBack(): boolean {
  return useContext(FrameBackContext);
}
